import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { baseApi, contentDispositionFilename } from '@/shared/api'
import type {
  ReconciliationBody,
  ReportFile,
  ReportHtml,
  ReportKind,
  ReportModifier,
} from '../model/types'

/**
 * Бинарный ответ выгрузки: успех — поток файла (`blob`), иначе — тело `ApiErrorDto` (JSON), чтобы
 * `normalizeError` разобрал код и статус. Тот же приём, что у выгрузок прайса (`entities/price`).
 */
const fileOrError = async (response: Response): Promise<Blob | unknown> =>
  response.ok ? response.blob() : response.json()

/** Имя файла из ответа; нет доступного `Content-Disposition` — имя по контракту (`fallback`). */
function fileFrom(raw: unknown, meta: FetchBaseQueryMeta | undefined, fallback: string): ReportFile {
  // Граница с бинарным ответом: на успехе `fileOrError` вернул Blob (ветка ошибки сюда не доходит)
  const blob = raw as Blob
  const header = meta?.response?.headers.get('content-disposition')
  return { blob, filename: contentDispositionFilename(header) ?? fallback }
}

/** Имя файла взаиморасчётов по контракту §8 (легаси-процедура `history_debts`). */
const DEBTS_XLSX_FILENAME = 'history.xlsx'

export const reportApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Готовый отчёт HTML (`GET /v1/reports/{kind}`): история отгрузок или взаиморасчёты из 1С.
     * Ответ — `{ html }` (форма вне схемы, `BE-161`). `404 report.no-data` — отчёт не сформирован,
     * `404 client.not-found` — чужой/несуществующий клиент в модификаторе. Менеджеру `?client=`/
     * `account` уходят в запрос как есть; клиенту модификатора нет — пара прав его не формирует.
     */
    getReportHtml: build.query<ReportHtml, { kind: ReportKind } & ReportModifier>({
      query: ({ kind, ...modifier }) => ({ url: `/reports/${kind}`, params: modifier }),
    }),

    /**
     * Взаиморасчёты за период HTML (`GET /v1/reports/debts/period`). `from`/`to` включительно,
     * период не глубже 3 месяцев (проверка на клиенте, бэк дублирует `400`). Ответ — `{ html }`.
     */
    getDebtsPeriodHtml: build.query<
      ReportHtml,
      { from: string; to: string } & ReportModifier
    >({
      query: ({ from, to, ...modifier }) => ({
        url: '/reports/debts/period',
        params: { from, to, ...modifier },
      }),
    }),

    /**
     * Взаиморасчёты за 30 дней, XLSX (`GET /v1/reports/debts.xlsx`). `extended=true` — подробный,
     * с позициями документов. Поток файла — мутация, результат потребитель сохраняет и сбрасывает.
     */
    downloadDebtsXlsx: build.mutation<ReportFile, { extended: boolean } & ReportModifier>({
      query: ({ extended, ...modifier }) => ({
        url: '/reports/debts.xlsx',
        params: { extended: String(extended), ...modifier },
        responseHandler: fileOrError,
        cache: 'no-store',
      }),
      transformResponse: (raw, meta) => fileFrom(raw, meta, DEBTS_XLSX_FILENAME),
    }),

    /** Взаиморасчёты за период, XLSX (`GET /v1/reports/debts/period.xlsx`). `extended` — как выше. */
    downloadDebtsPeriodXlsx: build.mutation<
      ReportFile,
      { from: string; to: string; extended: boolean } & ReportModifier
    >({
      query: ({ from, to, extended, ...modifier }) => ({
        url: '/reports/debts/period.xlsx',
        params: { from, to, extended: String(extended), ...modifier },
        responseHandler: fileOrError,
        cache: 'no-store',
      }),
      transformResponse: (raw, meta) => fileFrom(raw, meta, DEBTS_XLSX_FILENAME),
    }),

    /**
     * Запрос акта сверки (`POST /v1/reports/reconciliation`, `202` без тела). Fire-and-forget:
     * `commandId` нет, WS нет — результат не ждём, он появится позже в отчёте по взаиморасчётам
     * (бэк §6.1 T-408: ответный цикл 1С не работает). Инвалидации нет: ждать нечего.
     */
    requestReconciliation: build.mutation<void, ReconciliationBody>({
      query: (body) => ({ url: '/reports/reconciliation', method: 'POST', body }),
    }),
  }),
})

export const {
  useGetReportHtmlQuery,
  useGetDebtsPeriodHtmlQuery,
  useDownloadDebtsXlsxMutation,
  useDownloadDebtsPeriodXlsxMutation,
  useRequestReconciliationMutation,
} = reportApi
